import React, { useState, useEffect } from 'react';
import { get, post, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function SalesTargets() {
  const { user } = useAuth();
  const [targets, setTargets] = useState([]);
  const [salesStaff, setSalesStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth()+1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const isSuperAdmin = user?.role === 'super_admin';

  const load = () => {
    setLoading(true);
    get('/sales-targets?month='+filterMonth+'&year='+filterYear)
      .then(d => setTargets(d.data||[]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (isSuperAdmin) {
      get('/users').then(d => {
        const sales = (d.data||[]).filter(u => [
          'sales_manager',
          'content_sales',
          'brand_content_officer',
          'customer_experience_officer',
          'field_sales_representative'
        ].includes(u.role));
        setSalesStaff(sales);
      });
    }
  }, [filterMonth, filterYear]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try { await del('/sales-targets/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const years = [2024,2025,2026,2027,2028];

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Sales Targets & Commission</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Monthly targets and commission tracking</p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Set Target</button>
        )}
      </div>

      {/* Period filter */}
      <div className="search-bar" style={{marginBottom:20}}>
        <select className="form-control" style={{width:'auto'}} value={filterMonth} onChange={e=>setFilterMonth(parseInt(e.target.value))}>
          {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select className="form-control" style={{width:'auto'}} value={filterYear} onChange={e=>setFilterYear(parseInt(e.target.value))}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? <div className="spinner"></div> : targets.length === 0 ? (
        <div className="empty-state">
          <h3>No targets set for {MONTHS[filterMonth-1]} {filterYear}</h3>
          {isSuperAdmin && <p>Click Set Target to add a monthly target for your sales team.</p>}
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
          {targets.map(t => {
            const achieved = parseFloat(t.actual_achieved||0);
            const target = parseFloat(t.target_amount||0);
            const excess = parseFloat(t.excess_amount||0);
            const commission = parseFloat(t.calculated_commission||0);
            const progress = target > 0 ? Math.min(100, (achieved/target)*100) : 0;
            const exceeded = achieved >= target;

            return (
              <div key={t.id} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',boxShadow:'var(--shadow)'}}>
                {/* Card header */}
                <div style={{background:exceeded?'#556B2F':'#3B2A21',padding:'14px 18px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',color:'#A8893D',fontWeight:600}}>{t.staff_name}</div>
                    <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>{MONTHS[t.month-1]} {t.year}</div>
                  </div>
                  {exceeded
                    ? <span style={{fontSize:10,fontWeight:700,color:'#A8893D',background:'rgba(168,137,61,.15)',padding:'4px 10px',borderRadius:20,textTransform:'uppercase'}}>Target Met ✓</span>
                    : <span style={{fontSize:10,fontWeight:700,color:'rgba(242,239,231,.4)',background:'rgba(255,255,255,.1)',padding:'4px 10px',borderRadius:20,textTransform:'uppercase'}}>In Progress</span>
                  }
                </div>

                <div style={{padding:'16px 18px'}}>
                  {/* Progress bar */}
                  <div style={{marginBottom:16}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#8A8A8A',marginBottom:6}}>
                      <span>Progress</span>
                      <span style={{fontWeight:600,color:exceeded?'var(--paid)':'#3B2A21'}}>{progress.toFixed(0)}%</span>
                    </div>
                    <div style={{height:8,background:'#F2EFE7',borderRadius:4,overflow:'hidden'}}>
                      <div style={{height:'100%',borderRadius:4,background:exceeded?'#556B2F':'#A8893D',width:progress+'%',transition:'width .3s'}}></div>
                    </div>
                  </div>

                  {/* Numbers */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                    <div style={{padding:'10px 12px',background:'#F2EFE7',borderRadius:8}}>
                      <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>Target</div>
                      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',fontWeight:600,color:'#3B2A21'}}>{fmt(target)}</div>
                    </div>
                    <div style={{padding:'10px 12px',background:'#F2EFE7',borderRadius:8}}>
                      <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>Achieved</div>
                      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',fontWeight:600,color:exceeded?'var(--paid)':'#3B2A21'}}>{fmt(achieved)}</div>
                    </div>
                  </div>

                  {exceeded && (
                    <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 14px',marginBottom:12}}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
                        <span style={{color:'#2E6B3A',fontWeight:500}}>Excess Amount</span>
                        <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:15,fontWeight:600,color:'#2E6B3A'}}>{fmt(excess)}</span>
                      </div>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
                        <span style={{color:'#2E6B3A',fontWeight:500}}>Commission ({t.commission_rate}%)</span>
                        <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',fontWeight:700,color:'#556B2F'}}>{fmt(commission)}</span>
                      </div>
                    </div>
                  )}

                  {t.notes && <div style={{fontSize:12,color:'#8A8A8A',marginBottom:12,fontStyle:'italic'}}>{t.notes}</div>}

                  {isSuperAdmin && (
                    <button className="btn btn-danger btn-sm" style={{width:'100%',justifyContent:'center'}}
                      onClick={() => handleDelete(t.id)}>Delete Target</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <SetTargetModal
          salesStaff={salesStaff}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function SetTargetModal({ salesStaff, onClose, onSaved }) {
  const now = new Date();
  const [form, setForm] = useState({
    user_id:'', month:now.getMonth()+1, year:now.getFullYear(),
    target_amount:'', commission_rate:'5', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.user_id || !form.target_amount) { setError('Staff and target amount required'); return; }
    setSaving(true);
    try {
      await post('/sales-targets', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:460}}>
        <div className="modal-hdr">
          <span className="modal-title">Set Sales Target</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Sales Staff *</label>
            <select className="form-control" value={form.user_id} onChange={e=>setForm({...form,user_id:e.target.value})}>
              <option value="">Select staff</option>
              {salesStaff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role.replace('_',' ')})</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Month *</label>
              <select className="form-control" value={form.month} onChange={e=>setForm({...form,month:parseInt(e.target.value)})}>
                {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Year *</label>
              <input type="number" className="form-control" value={form.year}
                onChange={e=>setForm({...form,year:parseInt(e.target.value)})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Monthly Target (N) *</label>
              <input type="number" className="form-control" placeholder="e.g. 500000"
                value={form.target_amount} onChange={e=>setForm({...form,target_amount:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Commission Rate (%)</label>
              <input type="number" className="form-control" placeholder="e.g. 5"
                value={form.commission_rate} onChange={e=>setForm({...form,commission_rate:e.target.value})} />
              <div className="form-hint">Applied to excess amount above target</div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" placeholder="Any notes about this target..."
              value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
          </div>
          {form.target_amount && form.commission_rate && (
            <div style={{background:'#F2EFE7',borderRadius:8,padding:12,fontSize:12,color:'#556B2F'}}>
              Example: If staff achieves {fmt(parseFloat(form.target_amount||0)*1.2)}, excess is {fmt(parseFloat(form.target_amount||0)*0.2)}, commission = {fmt(parseFloat(form.target_amount||0)*0.2*parseFloat(form.commission_rate||0)/100)}
            </div>
          )}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':'Set Target'}
          </button>
        </div>
      </div>
    </div>
  );
}
